import React from 'react';
import type {
  WebsiteTemplateRegistryTemplateSummary,
  WebsiteUpsertRequest,
} from 'src/api/generated';
import { Text } from 'src/modules/shared/components/atoms/text';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import type { WebsitePreviewDevice } from '../../types/website-preview.types';
import { WebsiteScaledViewport } from './website-scaled-viewport';
import { WebsiteTemplateFallbackPreview } from './website-template-fallback-preview';

/** Rendering size of each device frame before scaling. */
const DEVICE_VIEWPORTS: Record<
  WebsitePreviewDevice,
  { width: number; height: number }
> = {
  desktop: { width: 1280, height: 960 },
  mobile: { width: 390, height: 720 },
};

/** Props for the inside of the site preview frame. */
interface WebsiteTemplatePreviewContentProps {
  /** Design shown in the preview, when one is selected. */
  template?: WebsiteTemplateRegistryTemplateSummary;
  /** Draft whose brand fills the descriptor-based fallback. */
  form: WebsiteUpsertRequest;
  /** Mock preview URL of the design with the draft brand. */
  iframeUrl?: string;
  /** Device frame the content renders for. */
  device: WebsitePreviewDevice;
}

/**
 * Fills the preview frame: the design's live mock preview at the device size,
 * the descriptor-based fallback for designs without one, or a prompt to pick
 * a design.
 *
 * @param props - Design, draft, preview URL, and device.
 * @returns The frame content.
 */
export const WebsiteTemplatePreviewContent: React.FC<
  WebsiteTemplatePreviewContentProps
> = ({ template, form, iframeUrl, device }) => {
  const { t } = useI18n();

  if (!template) {
    return (
      <div className="flex aspect-[4/3] items-center justify-center p-6 text-center">
        <Text size="small" color="neutral">
          {t('websites.templatePreview.noDesign')}
        </Text>
      </div>
    );
  }

  if (!iframeUrl) {
    return (
      <WebsiteTemplateFallbackPreview
        template={template}
        form={form}
        notice={t('websites.templatePreview.fallbackNotice')}
      />
    );
  }

  const viewport = DEVICE_VIEWPORTS[device];

  return (
    <WebsiteScaledViewport
      baseWidth={viewport.width}
      baseHeight={viewport.height}
    >
      <iframe
        title={t('websites.templatePreview.iframeTitle', {
          templateName: template.name,
        })}
        src={iframeUrl}
        width={viewport.width}
        height={viewport.height}
        className="block border-0 bg-white"
        loading="lazy"
        referrerPolicy="no-referrer"
        sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
      />
    </WebsiteScaledViewport>
  );
};
